import { useMemo } from "react";

import Tag from "ui-components/Tag";
import CategoriesDragDrop from "ui-components/Categories-DND";

import useStore from "./use-state";

const DroppedColumn = ({ column }: { column: string }) => (
  <Tag bgColorClass="bg-error-main" textColorClass="text-gray-100">
    <span className="cursor-move">{column}</span>
  </Tag>
);

const DraggableColumn = ({ column }: { column: string }) => (
  <Tag bgColorClass="bg-warning-main" textColorClass="text-gray-100">
    <span className="cursor-move">{column}</span>
  </Tag>
);

interface ColumnTypesProps {
  fileId: string;
  enableDrop?: boolean;
  onTypeChange?: (column: string, col_type: "numeric" | "char" | "date") => any;
  onDropStatusChange?: (column: string, status: "keep" | "drop") => any;
  orientation?: "vertical" | "horizontal";
  showTitle?: boolean;
}

const ColumnTypes: React.FC<ColumnTypesProps> = ({
  fileId,
  enableDrop = false,
  onTypeChange,
  onDropStatusChange,
  orientation,
  showTitle = true,
}) => {
  const {
    droppedColumns,
    columnTypes,
    deducedTypes,
    onStatusChange,
    dropAllColumns,
    retainAllColumns,
  } = useStore(fileId, enableDrop, onTypeChange, onDropStatusChange);

  const categories = useMemo(() => {
    let cats: ("numeric" | "char" | "date" | "drop")[] = [];
    const availableTypes = Array.from(new Set(Object.values(deducedTypes)));
    if (availableTypes.includes("int") || availableTypes.includes("float")) {
      cats.push("numeric");
    }
    if (availableTypes.includes("int") || availableTypes.includes("char")) {
      cats.push("char");
    }
    if (availableTypes.includes("date")) {
      cats.push("date");
    }
    if (enableDrop) {
      cats.push("drop");
    }
    return cats;
  }, [deducedTypes, enableDrop]);

  let items = Object.keys(deducedTypes)
    .sort((a, b) => a.toLocaleLowerCase().localeCompare(b.toLocaleLowerCase()))
    .map((column) => ({
      value: column,
      label:
        enableDrop && droppedColumns.includes(column) ? (
          <DroppedColumn key={column} column={column} />
        ) : deducedTypes[column] === "int" ? (
          <DraggableColumn key={column} column={column} />
        ) : (
          <Tag key={column}>{column}</Tag>
        ),
      draggable: enableDrop ? true : deducedTypes[column] === "int",
      allowed_categories: enableDrop
        ? [
            ...(deducedTypes[column] === "int"
              ? ["numeric", "char"]
              : deducedTypes[column] === "float"
              ? ["numeric"]
              : [deducedTypes[column]]),
            "drop",
          ]
        : ["numeric", "char"],
    }));

  return (
    <div>
      {showTitle && (
        <p className="font-bold underline text-2xl">Set Column Types</p>
      )}
      <div className="text-sm">
        <div className="flex flex-wrap items-center">
          The columns in <DraggableColumn column={"DRAGGABLE"} /> can be Numeric
          or Char. You can drag them around into their appropriate boxes.
        </div>{" "}
        {enableDrop && (
          <div className="flex flex-wrap items-center">
            You can also <DroppedColumn column="DROP" /> columns by moving them
            to the last box.
          </div>
        )}
      </div>
      {enableDrop && (
        <div className="text-right">
          <span
            className="text-primary-main cursor-pointer"
            onClick={dropAllColumns}
          >
            DROP ALL
          </span>{" "}
          |{" "}
          <span
            className="text-primary-main cursor-pointer"
            onClick={retainAllColumns}
          >
            RETAIN ALL
          </span>
        </div>
      )}
      <CategoriesDragDrop
        categories={categories}
        items={items}
        valueCategoryMap={columnTypes}
        // @ts-ignore
        onChange={onStatusChange}
        categoryClassName={"flex flex-wrap"}
        categoryLabels={{
          numeric: "Numeric Columns:",
          char: "Char Columns:",
          date: "Date Columns:",
          drop: "Dropped Columns:",
        }}
        orientation={orientation}
      />
    </div>
  );
};

export default ColumnTypes;
